import { doc, onSnapshot } from 'firebase/firestore';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { firestore } from '../services/firebase';
import { useAccount } from './AccountContext';

export interface BillingLedger {
  balance: number;
  accumulated_usage: number;
  isPAYG?: boolean;
  discount?: number;
  current_cycle_costs_by_function?: Record<string, number>;
  current_cycle_costs_by_team?: Record<string, number>;
  current_cycle_costs_by_user?: Record<string, number>;
}

interface BillingContextProps {
  ledger: BillingLedger | null;
  loading: boolean;
}

const BillingContext = createContext<BillingContextProps>({
  ledger: null,
  loading: true,
});

export const useBilling = () => useContext(BillingContext);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { account } = useAccount();
  const [ledger, setLedger] = useState<BillingLedger | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!account?.clientId) {
      setLedger(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ledgerRef = doc(firestore, 'clients', account.clientId, 'billingLedger', 'main');

    const unsubscribe = onSnapshot(
      ledgerRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setLedger(docSnap.data() as BillingLedger);
        } else {
          setLedger(null);
        }
        setLoading(false);
      },
      (_error) => {
        // Silently handle error or report to a monitoring service in the future
        setLedger(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [account?.clientId]);

  return <BillingContext.Provider value={{ ledger, loading }}>{children}</BillingContext.Provider>;
};
